import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "h-full w-full flex flex-col items-center justify-center space-y-6"
}
const _hoisted_2 = { class: "text-white font-semibold text-lg" }
const _hoisted_3 = {
  key: 1,
  "data-dusk": "viewer-loader",
  style: {"box-shadow":"inset 0 0 0 1000px rgba(0, 0, 0, 0.9)"},
  class: "absolute text-white inset-0 z-10 flex items-center justify-center"
}

import { computed, onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue';
import ResizeObserver from 'resize-observer-polyfill';
// @ts-ignore
import cornerstone from 'cornerstone-core/dist/cornerstone.min.js';
// @ts-ignore
import cornerstoneTools from 'cornerstone-tools/dist/cornerstoneTools.min.js';
import { IAnnotation, Tool } from '@/lib';
import { WebGlService } from '@/services/graphics/webgl.service';
import { LoadingIcon } from '@/lib/components/Loading';
import { BaseSnackbar } from '@/lib/components/Snackbar';
import { CornerstonePoint } from '@/models';
import BaseButton from '@/lib/components/Button/BaseButton.vue';
import { arrayEquals } from '@/helpers/array.helper';
import isEqual from 'lodash-es/isEqual';


export default /*#__PURE__*/_defineComponent({
  __name: 'ViewerItem',
  props: {
  enableTools: {
    type: Boolean,
    default: false
  },
  enableZoom: {
    type: Boolean,
    default: false
  },
  enablePan: {
    type: Boolean,
    default: false
  },
  enableReferenceLine: {
    type: Boolean,
    default: false
  },
  referenceLine: {
    type: Object as PropType<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      radius: number | null;
    }>,
    default: null
  },
  imageIds: {
    type: Array as PropType<string[]>,
    default: () => [],
    validator: (value: Array<string>) => !value.find((v) => !v.startsWith('http'))
  },
  translation: {
    type: Object as PropType<{ x: number; y: number }>,
    default: () => ({ x: 0, y: 0 })
  },
  currentImageIndex: {
    type: Number,
    default: 0
  },
  state: {
    type: Array as PropType<IAnnotation[]>,
    default: () => []
  },
  zoom: {
    type: Number,
    default: 1
  },
  zoomMin: {
    type: Number,
    default: 10
  },
  zoomMax: {
    type: Number,
    default: 1000
  },
  tool: {
    type: String,
    default: null
  },
  tools: {
    type: Array as PropType<Tool[]>,
    default: () => []
  },
  colourFilter: {
    type: Object as PropType<{ value: string; label: string }>,
    default: () => ({ value: '', label: '' })
  },
  dimensions: {
    type: Object as PropType<{
      widthMm: number;
      heightMm: number;
    }>,
    default: () => ({
      widthMm: 0,
      heightMm: 0
    })
  },
  readonly: {
    type: Boolean,
    default: false
  }
},
  emits: ["setZoom", "translate", "createAnnotation", "updateAnnotation", "removeAnnotation"],
  setup(__props, { emit }: { emit: ({
  (e: 'setZoom', value: { value: number; default: boolean }): void;
  (e: 'translate', value: { x: number; y: number }): void;
  (e: 'createAnnotation', annotation: IAnnotation): void;
  (e: 'updateAnnotation', annotation: IAnnotation): void;
  (e: 'removeAnnotation', id: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;




// template refs
const viewer = ref(null);
const transformedCanvas = ref(null);
const referenceLineCanvas = ref(null);

const loading = ref(false);
const isDefaultZoomSet = ref(false);
const isError = ref(false);
const loadingImageId = ref<string | null>(null); // current loading image id
const observer = ref<ResizeObserver | null>(null);
const image = ref({
  height: 0,
  width: 0
});

// transformations
const zooming = ref(false);
const translating = ref(false);
const enabled = ref(false);
const updating = ref<IAnnotation | null>(null);
const annotating = ref(false);
const initialScale = ref(1); // current image initial scale
const initialZoom = ref(1);

// colour transformations
const colourFilterSnackbar = ref(false);
const transformActive = ref(false);
const webGlService = ref<WebGlService | null>(null);

const pixelSpacing = computed(() => ({
  columnPixelSpacing: props.dimensions.widthMm / image.value.width,
  rowPixelSpacing: props.dimensions.heightMm / image.value.height
}));

// update displayed image
watch(() => props.imageIds, (current, previous) => {
  if (!arrayEquals(previous, current)) {
    if (enabled.value) {
      onImagesUpdate();
    } else {
      init();
    }
  }
});
watch(() => props.currentImageIndex, () => {
  if (isDefaultZoomSet.value) {
    loadImage(props.currentImageIndex, props.translation);
  } else {
    reload();
  }
});
// watch transformations update (zoom/pan/redFree)
watch(() => props.zoom, () => setZoom(props.zoom));
watch(() => props.translation, () => setTranslation(props.translation));
watch(() => props.colourFilter, () => setColourFilter(props.colourFilter));
watch(() => props.tool, (current, old) => setToolActive(current, old)); // activate selected tool
watch(() => props.state, (current, old) => {
  if (!isEqual(current, old)) {
    updateState();
  }
});
watch(() => props.referenceLine, () => drawReferenceLine());
watch(() => props.readonly, () => setReadOnly());

onMounted(() => {
  initialZoom.value = props.zoom;

  if (props.imageIds.length) {
    init();
  }
  cornerstone.metaData.addProvider(metadataProvider);
});
onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
  if (enabled.value) {
    const element = viewer.value as HTMLElement;
    element.removeEventListener('cornerstonetoolsmeasurementcompleted', (ev: any) =>
      emit('createAnnotation', ev.detail)
    );
    element.removeEventListener('cornerstonetoolsmeasurementmodified', (ev: any) =>
      emit('updateAnnotation', ev.details)
    );
    element.removeEventListener('cornerstonetoolsmeasurementremoved', (ev: any) =>
      emit('updateAnnotation', ev.detail.measurementData.uuid)
    );
    element.removeEventListener('cornerstoneimagerendered', (ev) => onRender(ev as CustomEvent));
    clearState();
    cornerstone.disable(element);
  }
});

// @ts-ignore
const metadataProvider = (type: string, imageId: string) => {
  if (
    type === 'imagePlaneModule' &&
    imageId === props.imageIds[props.currentImageIndex] &&
    (props.enableTools || props.readonly)
  ) {
    return pixelSpacing.value;
  }
};
const init = () => {
  cornerstoneTools.init();

  const element = viewer.value as HTMLElement;
  cornerstone.enable(element);

  // update viewport on element resize
  observer.value = new ResizeObserver((entries) => {
    window.requestAnimationFrame(() => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }
      if (isError.value) {
        isDefaultZoomSet.value = false;
      } else {
        reload();
      }
    });
  });
  observer.value.observe(element);

  onImagesUpdate();
  updateState();
  drawReferenceLine();

  const canvas = transformedCanvas.value as HTMLCanvasElement;
  webGlService.value = new WebGlService(canvas);
  setColourFilter(props.colourFilter);

  // Pan Tool
  if (props.enablePan) {
    cornerstoneTools.addToolForElement(viewer.value, cornerstoneTools.PanTool);
    cornerstoneTools.setToolActive('Pan', { mouseButtonMask: 4 }); // Pan on middle mouse button
    cornerstoneTools.setToolActive('Pan', { mouseButtonMask: 1 }); // Pan on left mouse button
  }
  addTools(props.tools);
  setReadOnly();

  // On annotate, emit the new state
  element.addEventListener('cornerstonetoolsmeasurementcompleted', (ev: any) => {
    emit('createAnnotation', ev.detail);
    annotating.value = false;
  });
  element.addEventListener('cornerstonetoolsmeasurementmodified', (ev: any) => {
    if (!updating.value) {
      const exists = props.state.find(
        (annotation) => annotation.measurementData.uuid === ev.detail.measurementData.uuid
      );
      if (exists) {
        updating.value = ev.detail;
      } else {
        annotating.value = true;
      }
    }
  });
  element.addEventListener('cornerstonetoolsmeasurementremoved', (ev: any) => {
    const exists = props.state.find(
      (annotation) => annotation.measurementData.uuid === ev.detail.measurementData.uuid
    );
    // If the current tool is the eraser and that the annotation exists, remove the annotation
    if (props.tool === 'Eraser' && exists) {
      emit('removeAnnotation', ev.detail.measurementData.uuid);
    } else {
      // Check if the annotation has been updated or created outside the image bound
      let handleOutsideImage = false;
      const handles = ev.detail.measurementData.handles;
      const newHandles: { [key: string]: CornerstonePoint } = {};
      Object.keys(handles).forEach((name: string) => {
        const handle = handles[name];
        if (name !== 'textBox' && (handle.x < 0 || handle.y < 0)) {
          handleOutsideImage = true;
        }
        newHandles[name] = {
          ...handle,
          allowedOutsideImage: true
        };
      });
      if (handleOutsideImage) {
        const annotation = {
          ...ev.detail,
          measurementData: {
            ...ev.detail.measurementData,
            handles: newHandles
          }
        };
        if (exists) {
          emit('updateAnnotation', annotation);
        } else {
          emit('createAnnotation', annotation);
        }
      }
    }
  });
  // If the image is rendered (image changed, transformation), update translate/zoom/shaders
  element.addEventListener('cornerstoneimagerendered', (ev) => onRender(ev as CustomEvent));
  window.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') {
      cancelDrawing(props.tool);
    }
  });

  // Add zoom (depends on the initial scale of the image)
  if (props.enableZoom) {
    cornerstoneTools.addToolForElement(viewer.value, cornerstoneTools.ZoomTool, {
      configuration: {
        minScale: (props.zoomMin * initialScale.value) / 100,
        maxScale: (props.zoomMax * initialScale.value) / 100,
        invert: true
      }
    });
    cornerstoneTools.setToolActive('Zoom', { mouseButtonMask: 2 });
  }
  enabled.value = true;
};

const reload = async () => {
  cornerstone.resize(viewer.value);
  await loadImage(props.currentImageIndex, props.translation);
  setDefaultZoom();
};
const setReadOnly = () => {
  // If readonly , disable all tools.
  if (props.readonly) {
    disableAllTools();
  } else {
    setToolsPassive();
  }
};
const updateState = () => {
  clearState();
  if (props.state) {
    props.state.map((annotation) => addState(annotation.toolName, annotation.measurementData));
  }

  loadImage(props.currentImageIndex, props.translation);
};
const clearState = () => {
  cornerstoneTools.getElementToolStateManager(viewer.value).clear(viewer.value);
};

// Mouse events
const onMouseDown = (ev: MouseEvent) => {
  if (!props.tool) {
    if (ev.buttons === 2) {
      zooming.value = true;
    }
    if (ev.buttons === 4 || ev.buttons === 1) {
      translating.value = true;
    }
  }
};

const onMouseUp = () => {
  if (!props.tool) {
    zooming.value = false;
    translating.value = false;
  }
  if (updating.value) {
    emit('updateAnnotation', updating.value);
    updating.value = null;
  }
};

const onContextMenu = (ev: Event) => {
  ev.preventDefault(); // disable context menu on Viewer
  return false;
};

// Tools management
const addTools = (tools: Array<Tool>) => {
  if (props.enableTools) {
    tools.map((tool) => {
      cornerstoneTools.addTool(cornerstoneTools[`${tool.cornerstoneName}Tool`], { configuration: tool.config });
      cornerstoneTools.setToolPassiveForElement(viewer.value, tool.cornerstoneName);
    });
  }
};

// on tool change, activate current tool and disable other tools
const setToolActive = (current: string, old: string) => {
  if (props.enableTools && current && !cornerstoneTools.isToolActiveForElement(viewer.value, current)) {
    const element = viewer.value;
    cornerstoneTools.setToolDisabledForElement(element, 'Pan'); // Disable pan when annotating
    cancelDrawing(old);
    disableAllTools();
    cornerstoneTools.setToolActiveForElement(element, current, {
      mouseButtonMask: 1
    }); // Activate tool on left mouse button
  } else {
    disableAllTools();
    if (props.enablePan) {
      cornerstoneTools.setToolActive('Pan', { mouseButtonMask: 4 }); // Pan on middle mouse button
      cornerstoneTools.setToolActive('Pan', { mouseButtonMask: 1 }); // Pan on left mouse button
    }
    if (props.enableZoom) {
      cornerstoneTools.setToolActive('Zoom', { mouseButtonMask: 2 });
    }
    if (props.enableTools) {
      setToolsPassive();
    }
  }
};

const disableAllTools = () => {
  props.tools.map((tool) => cornerstoneTools.setToolEnabledForElement(viewer.value, tool.cornerstoneName));
};

const setToolsPassive = () => {
  props.tools.map((tool) => cornerstoneTools.setToolPassiveForElement(viewer.value, tool.cornerstoneName));
};

const addState = (toolName: string, state: any) => {
  cornerstoneTools
    .getElementToolStateManager(viewer.value)
    .addImageIdToolState(props.imageIds[props.currentImageIndex], toolName, state);
};

const cancelDrawing = (toolName: string) => {
  if (toolName && annotating.value) {
    const element = viewer.value;
    const toolState = cornerstoneTools.getToolState(element, toolName);
    if (toolState && toolState.data && toolState.data.length) {
      const data = toolState.data[toolState.data.length - 1];
      if (toolName === 'FreehandRoi') {
        const tool = cornerstoneTools.getToolForElement(element, toolName);
        tool.cancelDrawing(element);
      } else {
        cornerstoneTools.removeToolState(element, toolName, data);
        cornerstone.updateImage(element);
      }
    }
  }
};

// Transformations
const onRender = (ev: CustomEvent) => {
  if (zooming.value && props.enableZoom && ev.detail.viewport?.scale) {
    emit('setZoom', {
      value: (ev.detail.viewport.scale * 100) / initialScale.value,
      default: false
    });
  } else if (translating.value && props.enablePan) {
    emit('translate', ev.detail.viewport.translation);
  }

  if (transformActive.value && webGlService.value !== null) {
    const element = viewer.value;
    webGlService.value.setSource(cornerstone.getEnabledElement(element).canvas);
    webGlService.value.draw();
  }

  drawReferenceLine();
};

const setZoom = (zoom: number) => {
  if (props.enableZoom) {
    const element = viewer.value;
    const viewport = cornerstone.getViewport(element);
    if (viewport) {
      viewport.scale = zoom * initialScale.value;
      cornerstone.setViewport(element, viewport);
    }
  }
};

const setTranslation = (translation: { x: number; y: number }) => {
  if (props.enablePan) {
    const element = viewer.value;
    const viewport = cornerstone.getViewport(element);
    if (viewport) {
      viewport.translation = translation;
      cornerstone.setViewport(element, viewport);
    }
  }
};

const setColourFilter = (filter: { value: string, label: string }) => {
  const element = viewer.value;
  if (filter.value && webGlService.value !== null) {
    webGlService.value.setProgram('image', filter.value);
    webGlService.value.setSource(cornerstone.getEnabledElement(element).canvas);
    webGlService.value.draw();

    transformActive.value = true;
    cornerstone.getEnabledElement(element).canvas.style.display = 'none';
    colourFilterSnackbar.value = true;
  } else {
    transformActive.value = false;
    cornerstone.getEnabledElement(element).canvas.style.display = 'block';
    colourFilterSnackbar.value = false;
  }
};

const drawReferenceLine = () => {
  if (props.enableReferenceLine) {
    const canvas = referenceLineCanvas.value as HTMLCanvasElement;
    if (canvas.getContext) {
      const context = canvas.getContext('2d');
      if (context) {
        context.clearRect(0, 0, canvas.width, canvas.height);
        if (props.referenceLine) {
          const midX = canvas.width / 2;
          const midY = canvas.height / 2;
          const scaleX = (initialScale.value * canvas.width) / canvas.clientWidth;
          const scaleY = (initialScale.value * canvas.height) / canvas.clientHeight;
          const x1 = midX + (props.referenceLine.x1 - image.value.width / 2) * scaleX;
          const y1 = midY + (props.referenceLine.y1 - image.value.height / 2) * scaleY;

          context.beginPath();
          context.lineWidth = 2;
          context.strokeStyle = '#a9d96c';
          context.fillStyle = '#a9d96c';
          if (props.referenceLine.radius) {
            const radiusX = props.referenceLine.radius * scaleX;
            const radiusY = props.referenceLine.radius * scaleY;
            context.fillRect(x1, y1, 2, 2);
            context.ellipse(x1, y1, radiusX, radiusY, 0, 0, 2 * Math.PI);
          } else {
            const x2 = midX + (props.referenceLine.x2 - image.value.width / 2) * scaleX;
            const y2 = midY + (props.referenceLine.y2 - image.value.height / 2) * scaleY;
            context.moveTo(x1, y1);
            context.lineTo(x2, y2);
          }
          context.stroke();
        }
      }
    }
  }
};

// Images
const onImagesUpdate = async () => {
  if (props.imageIds?.length) {
    await loadImage(props.currentImageIndex);
    setDefaultZoom();
    emit('translate', { x: 0, y: 0 }); // reset translation
  }
};

const setDefaultZoom = () => {
  try {
    isDefaultZoomSet.value = false;
    const enabledElement = cornerstone.getEnabledElement(viewer.value);
    if (enabledElement?.image && enabledElement?.canvas) {
      const width = enabledElement.image.width;
      const height = enabledElement.image.height;
      const verticalScale = enabledElement.canvas.height / (height * initialScale.value);
      const horizontalScale = enabledElement.canvas.width / (width * initialScale.value);

      // Apply new scale
      initialZoom.value = Math.min(horizontalScale, verticalScale);
      emit('setZoom', { value: initialZoom.value * 100, default: true }); // reset zoom
      isDefaultZoomSet.value = true;
    }
  } catch (e) {
    console.error(e);
  }
};

/**
   * Load image and apply transformation
   * @param index Index of the image to load
   * @param translation Translation to apply
   */
const loadImage = async (index: number, translation = { x: 0, y: 0 }) => {
  const id = props.imageIds[index];
  if (id) {
    const element = viewer.value;
    isError.value = false;
    loading.value = true;
    loadingImageId.value = id;
    try {
      // Cache image
      const cornerstoneImage = await cornerstone.loadAndCacheImage(id);
      const viewport = cornerstone.getDefaultViewportForImage(element, cornerstoneImage);
      initialScale.value = viewport.scale;
      // Apply zoom and translation on image viewport
      cornerstone.setViewport(element, {
        ...viewport,
        scale: props.zoom * initialScale.value,
        translation
      });
      if (loadingImageId.value === id) {
        image.value = {
          width: cornerstoneImage.width,
          height: cornerstoneImage.height
        };
        cornerstone.displayImage(element, cornerstoneImage);
      }
    } catch (e) {
      isError.value = true;
    } finally {
      loading.value = false;
    }
  }
};

const dismissColourFilterSnackbar = () => {
  colourFilterSnackbar.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _mergeProps({
    ref_key: "viewer",
    ref: viewer,
    class: "h-full w-full relative",
    style: {"background-color":"#0e0e0e"}
  }, _ctx.$attrs, {
    onContextmenu: _cache[0] || (_cache[0] = ($event: any) => (onContextMenu($event))),
    onMouseup: _cache[1] || (_cache[1] = ($event: any) => (onMouseUp())),
    onMousedown: _cache[2] || (_cache[2] = ($event: any) => (onMouseDown($event)))
  }), [
    _renderSlot(_ctx.$slots, "default"),
    _createElementVNode("canvas", {
      ref_key: "transformedCanvas",
      ref: transformedCanvas,
      class: _normalizeClass(["transformed-canvas h-full w-full", [transformActive.value ? '' : 'hidden']])
    }, null, 2),
    _createElementVNode("canvas", {
      ref_key: "referenceLineCanvas",
      ref: referenceLineCanvas,
      class: _normalizeClass(["h-full w-full absolute inset-0 z-10", [__props.enableReferenceLine ? '' : 'hidden']])
    }, null, 2),
    (isError.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.$t('platform.scans.loading-error')), 1),
          _createVNode(BaseButton, {
            "left-icon": "reload",
            size: "large",
            onClick: reload
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('platform.scans.reload')), 1)
            ]),
            _: 1
          })
        ]))
      : (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_unref(LoadingIcon))
          ]))
        : _createCommentVNode("", true),
    (colourFilterSnackbar.value)
      ? (_openBlock(), _createBlock(_unref(BaseSnackbar), {
          key: 2,
          "dismiss-after": 5000,
          "auto-dismiss": "",
          label: __props.colourFilter.label + ' has been applied',
          class: "z-40",
          icon: "check",
          color: "success",
          dark: "",
          onDismiss: dismissColourFilterSnackbar
        }, null, 8, ["label"]))
      : _createCommentVNode("", true)
  ], 16))
}
}

})